import { Series, Tool } from 'Explore/types';
import { rem } from 'polished';
import React from 'react';
import { useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { Icon } from '@unmind/design-system';
import { HeadingText } from '../../Shared/Typography';
import BodyText from '../../Shared/Typography/BodyText';
import Container from '../../Shared/Container';
import LoadingIndicator from '../../Shared/LoadingIndicator';
import { small, medium } from '../../utils';
import BreadcrumbNavigation from '../BreadcrumbNavigation/BreadcrumbNavigation';
import { filterOutCollectionsFromContent } from '../Collections/collectionsHelpers';
import ContentSection from '../Content/ContentSection';
import { TemporaryStateContainer } from '../ExplorePage';
import GenericError from '../GenericError/GenericError';
import HeroBannerSection from '../HeroBanner/HeroBannerSection';
import SubCategorySection from '../SubCategory/SubCategorySection';
import { CategoryIconNames } from '../categoryIcons';
import {
  categoryContent_exploreCategory,
  categoryContent_exploreCategory_content,
} from './__generated__/categoryContent';
import useExploreCategoryQuery from './useExploreCategoryQuery';

const HeaderContainer = styled.div`
  padding-top: ${rem(32)};

  ${small(css`
    padding-top: ${rem(24)};
  `)}

  ${medium(css`
    padding-top: ${rem(30)};
  `)}
`;

const HeadingContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${rem(9)};
`;

const StyledHeading = styled(HeadingText).attrs(({ theme }) => ({
  level: 1,
  sizes: [theme.typography.fontSizes.fontSize24],
  weight: theme.typography.fontWeights.medium,
}))`
  text-align: left;
`;

const SubHeading = styled(BodyText).attrs(({ theme }) => ({
  color: theme.colors.text.primary,
  sizes: [theme.typography.fontSizes.fontSize16],
}))`
  padding-top: 8px;
`;

const CategoryScreenInner = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-bottom: ${rem(28)};
  padding-top: ${rem(32)};

  ${small(css`
    padding-top: ${rem(40)};
  `)};
`;

const CategoryPage = () => {
  const { category: categorySlug } = useParams<{
    category: string;
  }>();

  const { data, loading, error, refetch } = useExploreCategoryQuery({
    variables: { slug: categorySlug },
  });

  const category = data?.exploreCategory;
  const heroBanner = data?.exploreCategory?.featuredContent;
  const subCategories = data?.exploreCategory?.subCategories;
  const content = filterOutCollectionsFromContent(
    data?.exploreCategory?.content as (Series | Tool)[],
  );

  const assetToken = data?.getAssetToken?.assetToken;

  if (loading) {
    return (
      <TemporaryStateContainer>
        <LoadingIndicator />
      </TemporaryStateContainer>
    );
  }

  if (error) {
    return (
      <TemporaryStateContainer>
        <GenericError retry={true} onRetry={refetch} />
      </TemporaryStateContainer>
    );
  }

  const iconName = category?.slug
    ? CategoryIconNames[category.slug as keyof typeof CategoryIconNames]
    : undefined;

  return (
    <Container>
      <HeaderContainer>
        <BreadcrumbNavigation />
        <HeadingContainer>
          {iconName ? <Icon icon={iconName} size="large" /> : null}
          <StyledHeading aria-roledescription="heading">
            {category?.title ?? ''}
          </StyledHeading>
        </HeadingContainer>
        {category?.summary ? (
          <SubHeading aria-roledescription="subheading">
            {category.summary}
          </SubHeading>
        ) : null}
      </HeaderContainer>
      <CategoryScreenInner>
        {heroBanner && (
          <HeroBannerSection
            heroBannerContent={heroBanner}
            currentCategory={category as categoryContent_exploreCategory}
            source={'explore-category-hero-banner'}
            assetToken={data?.getAssetToken?.assetToken}
          />
        )}
        {subCategories && subCategories.length > 0 ? (
          <SubCategorySection
            title={category?.title}
            subCategories={subCategories}
            currentCategory={category as categoryContent_exploreCategory}
          />
        ) : null}
        {content && content.length > 0 ? (
          <ContentSection
            content={content as categoryContent_exploreCategory_content[]}
            source="explore-content-page"
            assetToken={assetToken}
          />
        ) : null}
      </CategoryScreenInner>
    </Container>
  );
};

export default CategoryPage;
